import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../api/axios';
import { handleApiError } from '../../pages/UBTAdmin/ProjectDetails/ProjectHelper/ViewProjectHandleApiError';

export interface ManageProjectState {
  valueCompassList: any;
  focusAreaList: any[] | null;
  metricList: any[] | null;
  datasourceList: any[] | null;
  manageProjectDependentDropdownLoading: boolean;
  manageProjectDependentDropdownLoadingError: string | null;
  createProjectLoading?: boolean;
  createProjectError?: string | null;
  createdProject?: any;
  updateProjectLoading?: boolean;
  updateProjectError?: string | null;
  updatedProject?: any;
}

const initialState: ManageProjectState = {
  valueCompassList: null,
  focusAreaList: null,
  metricList: null,
  datasourceList: null,
  manageProjectDependentDropdownLoading: false,
  manageProjectDependentDropdownLoadingError: null,
  createProjectLoading: false,
  createProjectError: null,
  createdProject: null,
  updateProjectLoading: false,
  updateProjectError: null,
  updatedProject: null,
};

/**
 * Fetch focus area value compass for a project.
 * Example URL: /ubt/projects/02/getFocusAreaValueCompass?operation=getvaluecompass&focusid=45
 */
export const fetchFocusAreaValueCompassList = createAsyncThunk(
  'manageProject/fetchFocusAreaValueCompassList',
  async (
    { 
        regioncd, 
        focusId, 
        nuid }: { regioncd: string | null; focusId: string | number; nuid?: string | null },
    { rejectWithValue, signal },
  ) => {
    try {
      const url = `/ubt/projects/${encodeURIComponent(String(regioncd))}/getFocusAreaValueCompass`;
      const params = { 
        operation: 'getvaluecompass', 
        focusid: String(focusId) };
      const options: any = {
        signal,
        params,
        headers: {
          accept: 'application/json',
          ...(nuid ? { 'x-nuid': nuid } : {}),
        },
      };

      const response = await api.get(url, options);
      return response.data;
    } catch (err: any) {
      return handleApiError(err, rejectWithValue, 'Failed to fetch focus area value compass');
    }
  }
);

// Thunk: create a project via POST /ubt/project
export const createProject = createAsyncThunk(
  'manageProject/createProject',
  async ({ payload, nuid }: { payload: any; nuid?: string | null }, { rejectWithValue }) => {
    try {
      const url = '/ubt/project';
      const options: any = {
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
          ...(nuid ? { 'x-nuid': nuid } : {}),
        },
      };

      const response = await api.post(url, payload, options);
      return response.data;
    } catch (err: any) {
      return handleApiError(err, rejectWithValue, 'Failed to create project');
    }
  },
);

// Thunk: update a project via PATCH /ubt/project/:id
export const updateProject = createAsyncThunk(
  'manageProject/updateProject',
  async ({ projectId, payload, nuid }: { projectId: string | number; payload: any; nuid?: string | null }, { rejectWithValue }) => {
    try {
      const url = `/ubt/project/${encodeURIComponent(String(projectId))}`;
      const options: any = {
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
          ...(nuid ? { 'x-nuid': nuid } : {}),
        },
      };

      const response = await api.patch(url, payload, options);
      return response.data;
    } catch (err: any) {
      return handleApiError(err, rejectWithValue, 'Failed to update project');
    }
  },
);


/**
 * Fetch focus area details (operation=getFocusArea)
 * Example URL: /ubt/projects/02/getFocusAreaValueCompass?operation=getFocusArea&focusid=45
 */
export const fetchFocusAreaList = createAsyncThunk(
  'manageProject/fetchFocusAreaList',
  async (
    { 
     regioncd,
     nuid }: { regioncd: string | null; nuid?: string | null },
    { rejectWithValue, signal },
  ) => {
    try {
      const url = `/ubt/projects/${encodeURIComponent(String(regioncd))}/getFocusAreaValueCompass`;
      const params = { operation: 'getFocusArea'};
      const options: any = {
        signal,
        params,
        headers: {
          accept: 'application/json',
          ...(nuid ? { 'x-nuid': nuid } : {}),
        },
      };

      const response = await api.get(url, options);
      return response.data;
    } catch (err: any) {
      return handleApiError(err, rejectWithValue, 'Failed to fetch focus area');
    }
  }
);

/**
 * Fetch metric list for a focus/value compass selection.
 * Example URL: /ubt/projects/02/getdetailSelection?operation=getmetriclist&focusid=42&valuecompassid=41
 */
export const fetchMetricList = createAsyncThunk(
  'manageProject/fetchMetricList',
  async (
    { regioncd, focusId, valueCompassId, nuid }: { regioncd: string | null; focusId: string | number; valueCompassId: string | number; nuid?: string | null },
    { rejectWithValue, signal },
  ) => {
    try {
      const url = `/ubt/projects/${encodeURIComponent(String(regioncd))}/getdetailSelection`;
      const params = { operation: 'getmetriclist', focusid: String(focusId), valuecompassid: String(valueCompassId) };
      const options: any = {
        signal,
        params,
        headers: {
          accept: 'application/json',
          ...(nuid ? { 'x-nuid': nuid } : {}),
        },
      };

      const response = await api.get(url, options);
      // normalize response into an object with a `metrics` array for predictable shape
      const data = response.data ?? {};
      let metrics: any[] = [];
      if (Array.isArray((data).metrics)) {
        metrics = (data).metrics;
      } else if (Array.isArray(data)) {
        metrics = data;
      } else if (Array.isArray((data).items)) {
        metrics = (data).items;
      } else if (Array.isArray((data).list)) {
        metrics = (data).list;
      } else {
        metrics = [];
      }
      return { metrics };
    } catch (err: any) {
      return handleApiError(err, rejectWithValue, 'Failed to fetch metric list');
    }
  }
);

    /**
     * Fetch datasource list for a selected metric.
     * Example URL: /ubt/projects/02/getdetailSelection?operation=getdatasourcelist&focusid=42&valuecompassid=41&metricid=48
     */
    export const fetchDatasourceList = createAsyncThunk(
      'manageProject/fetchDatasourceList',
      async (
        { regioncd, focusId, valueCompassId, metricId, nuid }: { regioncd: string | null; focusId: string | number; valueCompassId: string | number; metricId: string | number; nuid?: string | null },
        { rejectWithValue, signal },
      ) => {
        try {
          const url = `/ubt/projects/${encodeURIComponent(String(regioncd))}/getdetailSelection`;
          const normalizeParamNumber = (value: string | number): string | number => {
            if (typeof value === 'number') return value;
            const parsed = Number(value);
            return Number.isNaN(parsed) ? value : parsed;
          };

          const params = {
            operation: 'getdatasourcelist',
            focusid: normalizeParamNumber(focusId),
            valuecompassid: normalizeParamNumber(valueCompassId),
            metricid: normalizeParamNumber(metricId),
          };
          const options: any = {
            signal,
            params,
            headers: {
              accept: 'application/json',
              ...(nuid ? { 'x-nuid': nuid } : {}),
            },
          };

          const response = await api.get(url, options);
          const data = response.data ?? {};
          // Accept multiple possible shapes: array directly, { dataSources: [...] }, { items: [...] }, { list: [...] }
          if (Array.isArray(data)) return data;
          if (Array.isArray((data).dataSources)) return (data).dataSources;
          return (data).items ?? (data).list ?? [];
        } catch (err: any) {
          return handleApiError(err, rejectWithValue, 'Failed to fetch datasource list');
        }
      }
    );

const manageProjectSlice = createSlice({
  name: 'manageProject',
  initialState,
  reducers: {
    resetManageProjectState(state) {
      state.valueCompassList = null;
      state.focusAreaList = null;
      state.manageProjectDependentDropdownLoading = false;
      state.manageProjectDependentDropdownLoadingError = null;
    },
    setValueCompassList(state, action: PayloadAction<any>) {
      state.valueCompassList = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFocusAreaValueCompassList.pending, (state) => {
        state.manageProjectDependentDropdownLoading = true;
        state.manageProjectDependentDropdownLoadingError = null;
      })
      .addCase(fetchFocusAreaValueCompassList.fulfilled, (state, action: PayloadAction<any>) => {
        state.manageProjectDependentDropdownLoading = false;
        state.manageProjectDependentDropdownLoadingError = null;
        state.valueCompassList = action.payload ?? null;
      })
      .addCase(fetchFocusAreaValueCompassList.rejected, (state, action) => {
        state.manageProjectDependentDropdownLoading = false;
        state.manageProjectDependentDropdownLoadingError = (action.payload as string) ?? action.error?.message ?? 'Failed to fetch focus area value';
      });
    builder
      .addCase(fetchFocusAreaList.pending, (state) => {
        state.manageProjectDependentDropdownLoading = true;
        state.manageProjectDependentDropdownLoadingError = null;
      })
      .addCase(fetchFocusAreaList.fulfilled, (state, action: PayloadAction<any>) => {
        state.manageProjectDependentDropdownLoading = false;
        state.manageProjectDependentDropdownLoadingError = null;
        state.focusAreaList = action.payload ?? null;
      })
      .addCase(fetchFocusAreaList.rejected, (state, action) => {
        state.manageProjectDependentDropdownLoading = false;
        state.manageProjectDependentDropdownLoadingError = (action.payload as string) ?? action.error?.message ?? 'Failed to fetch focus area';
      });
    builder
      .addCase(fetchMetricList.pending, (state) => {
        state.manageProjectDependentDropdownLoading = true;
        state.manageProjectDependentDropdownLoadingError = null;
      })
      .addCase(fetchMetricList.fulfilled, (state, action: PayloadAction<any[]>) => {
        state.manageProjectDependentDropdownLoading = false;
        state.manageProjectDependentDropdownLoadingError = null;
        state.metricList = action.payload ?? null;
      })
      .addCase(fetchMetricList.rejected, (state, action) => {
        state.manageProjectDependentDropdownLoading = false;
        state.manageProjectDependentDropdownLoadingError = (action.payload as string) ?? action.error?.message ?? 'Failed to fetch metric list';
      });
      builder
        .addCase(fetchDatasourceList.pending, (state) => {
          state.manageProjectDependentDropdownLoading = true;
          state.manageProjectDependentDropdownLoadingError = null;
        })
        .addCase(fetchDatasourceList.fulfilled, (state, action: PayloadAction<any[]>) => {
          state.manageProjectDependentDropdownLoading = false;
          state.manageProjectDependentDropdownLoadingError = null;
          state.datasourceList = action.payload ?? null;
        })
        .addCase(fetchDatasourceList.rejected, (state, action) => {
          state.manageProjectDependentDropdownLoading = false;
          state.manageProjectDependentDropdownLoadingError = (action.payload as string) ?? action.error?.message ?? 'Failed to fetch datasource list';
        });
        // createProject handlers
        builder
          .addCase(createProject.pending as any, (state) => {
            state.createProjectLoading = true;
            state.createProjectError = null;
          })
          .addCase(createProject.fulfilled as any, (state, action: PayloadAction<any>) => {
            state.createProjectLoading = false;
            state.createProjectError = null;
            state.createdProject = action.payload ?? null;
          })
          .addCase(createProject.rejected as any, (state, action) => {
            state.createProjectLoading = false;
            state.createProjectError = (action.payload as string) ?? action.error?.message ?? 'Failed to create project';
          });
        // updateProject handlers
        builder
          .addCase(updateProject.pending as any, (state) => {
            (state as any).updateProjectLoading = true;
            (state as any).updateProjectError = null;
          })
          .addCase(updateProject.fulfilled as any, (state, action: PayloadAction<any>) => {
            (state as any).updateProjectLoading = false;
            (state as any).updateProjectError = null;
            (state as any).updatedProject = action.payload ?? null;
          })
          .addCase(updateProject.rejected as any, (state, action) => {
            (state as any).updateProjectLoading = false;
            (state as any).updateProjectError = (action.payload as string) ?? action.error?.message ?? 'Failed to update project';
          });
  },
});

export const { resetManageProjectState, setValueCompassList } = manageProjectSlice.actions;

export const selectValueCompassList = (state: any) => state.manageProjectSlice?.valueCompassList ?? null;
export const selectManageProjectDependentDropdownLoading = (state: any) => state.manageProjectSlice?.manageProjectDependentDropdownLoading ?? false;
export const manageProjectDependentDropdownLoading = (state: any) => state.manageProjectSlice?.manageProjectDependentDropdownLoading ?? false;
export const selectManageProjectError = (state: any) => state.manageProjectSlice?.error ?? null;

export const selectFocusAreaList = (state: any) => state.manageProjectSlice?.focusAreaList ?? null;
export const selectMetricList = (state: any) => state.manageProjectSlice?.metricList ?? null;
export const selectDatasourceList = (state: any) => state.manageProjectSlice?.datasourceList ?? null;

export const selectCreateProjectLoading = (state: any) => state.manageProjectSlice?.createProjectLoading ?? false;
export const selectCreateProjectError = (state: any) => state.manageProjectSlice?.createProjectError ?? null;
export const selectCreatedProject = (state: any) => state.manageProjectSlice?.createdProject ?? null;

export const selectUpdateProjectLoading = (state: any) => state.manageProjectSlice?.updateProjectLoading ?? false;
export const selectUpdateProjectError = (state: any) => state.manageProjectSlice?.updateProjectError ?? null;
export const selectUpdatedProject = (state: any) => state.manageProjectSlice?.updatedProject ?? null;

export default manageProjectSlice.reducer;
